import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

// ─── CONFIG ───────────────────────────────────────────────────
// const API_BASE = "http://localhost:5000/api";
const API_BASE = "http://192.168.0.57:5000/api";
// ─── MOCK DATA ────────────────────────────────────────────────
const mockBookingContext = {
  pickup: "Bhubaneswar Airport",
  dropoff: "Bhubaneswar Airport",
  pickupDate: "2025-05-10",
  pickupTime: "10:00",
  returnDate: "2025-05-13",
  returnTime: "10:00",
  driverAge: "26-35",
  diffDrop: false,
  days: 3,
};

const mockCars = [
  { car_id: 1, car_name: "Swift Dzire", car_brand: "Maruti Suzuki", cat_name: "Sedan", group_name: "Small Car", group_id: 1, car_passgrno: 5, car_luggno: 2, car_transmission: "Manual", car_airconditionr: "Yes", car_dispinhome: 1, car_image: "", image_alt: "Swift Dzire", car_desc: "Compact and fuel-efficient sedan perfect for city and highway driving.", base_price: 1200, badge: "Economy", featured: false },
  { car_id: 2, car_name: "Innova Crysta", car_brand: "Toyota", cat_name: "MPV", group_name: "7 Seater", group_id: 4, car_passgrno: 7, car_luggno: 4, car_transmission: "Automatic", car_airconditionr: "Yes", car_dispinhome: 1, car_image: "", image_alt: "Innova Crysta", car_desc: "Premium 7-seater MPV ideal for families and corporate travel.", base_price: 2800, badge: "Popular", featured: true },
  { car_id: 3, car_name: "Creta", car_brand: "Hyundai", cat_name: "Compact SUV", group_name: "SUV", group_id: 2, car_passgrno: 5, car_luggno: 3, car_transmission: "Automatic", car_airconditionr: "Yes", car_dispinhome: 1, car_image: "", image_alt: "Hyundai Creta", car_desc: "Feature-loaded compact SUV with bold design and premium comfort.", base_price: 2200, badge: "Trending", featured: false },
  { car_id: 4, car_name: "BMW 5 Series", car_brand: "BMW", cat_name: "Luxury Sedan", group_name: "Luxury", group_id: 3, car_passgrno: 5, car_luggno: 3, car_transmission: "Automatic", car_airconditionr: "Yes", car_dispinhome: 1, car_image: "", image_alt: "BMW 5 Series", car_desc: "Executive luxury sedan delivering unmatched performance and refinement.", base_price: 6500, badge: "Luxury", featured: false },
  { car_id: 5, car_name: "Fortuner", car_brand: "Toyota", cat_name: "SUV", group_name: "SUV", group_id: 2, car_passgrno: 7, car_luggno: 4, car_transmission: "Automatic", car_airconditionr: "Yes", car_dispinhome: 1, car_image: "", image_alt: "Toyota Fortuner", car_desc: "Powerful full-size SUV with commanding road presence and off-road capability.", base_price: 4200, badge: "Off-Road", featured: false },
  { car_id: 6, car_name: "Alto K10", car_brand: "Maruti Suzuki", cat_name: "Hatchback", group_name: "Small Car", group_id: 1, car_passgrno: 4, car_luggno: 1, car_transmission: "Manual", car_airconditionr: "Yes", car_dispinhome: 1, car_image: "", image_alt: "Alto K10", car_desc: "Ultra-compact city hatchback with outstanding fuel economy.", base_price: 800, badge: "Budget", featured: false },
];

const mockExtras = [
  { id: 1, name: "GPS Navigation", icon: "🗺️", price: 150, unit: "per day", desc: "Full India maps with live traffic" },
  { id: 2, name: "Child Seat", icon: "👶", price: 200, unit: "per day", desc: "Certified safety seat for infants" },
  { id: 3, name: "Additional Driver", icon: "👤", price: 300, unit: "total", desc: "Add a second named driver" },
  { id: 4, name: "Full Insurance", icon: "🛡️", price: 400, unit: "total", desc: "Zero excess comprehensive cover" },
  { id: 5, name: "Roadside Assist", icon: "🔧", price: 100, unit: "total", desc: "24/7 on-road breakdown support" },
];

const mockInsurance = [
  { ins_id: 1, ins_item: "Basic Protection", ins_chargeperday: 200, slab_prices: { 1: 180, 2: 160, 3: 140 }, groups: [] },
  { ins_id: 2, ins_item: "Comprehensive Cover", ins_chargeperday: 400, slab_prices: { 1: 380, 2: 350, 3: 300 }, groups: [] },
];

const mockGroups = [
  { group_id: 0, group_name: "All Categories" },
  { group_id: 1, group_name: "Small Car" },
  { group_id: 2, group_name: "SUV" },
  { group_id: 3, group_name: "Luxury" },
  { group_id: 4, group_name: "7 Seater" },
];

// ─── FETCH CAR PRICE FROM API ─────────────────────────────────
const fetchCarPrice = async (car, ctx) => {
  try {
    const res = await fetch(`${API_BASE}/price/calculate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ car_id: car.car_id, days: ctx.days, pickupDate: ctx.pickupDate, returnDate: ctx.returnDate }),
    });
    const data = await res.json();
    return data?.final_price || car.base_price;
  } catch (e) {
    return car.base_price;
  }
};

// ─── FETCH HOOK ───────────────────────────────────────────────
function useFetch(endpoint, fallback) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}${endpoint}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setData(fallback); setLoading(false); });
  }, [endpoint]);

  return { data, loading, error };
}

// ─── STYLES ───────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --gold: #C9A84C;
    --gold-light: #E8C97A;
    --dark: #0A0A0F;
    --dark2: #111118;
    --dark3: #1A1A24;
    --surface: #16161F;
    --border: rgba(201,168,76,0.18);
    --text: #E8E8F0;
    --muted: #7A7A9A;
    --white: #FFFFFF;
    --radius: 12px;
    --green: #1D9E75;
    --red: #E24B4A;
  }
  * { margin:0; padding:0; box-sizing:border-box; }
  html { scroll-behavior:smooth; }
  body { font-family:'DM Sans',sans-serif; background:var(--dark); color:var(--text); overflow-x:hidden; }

  /* ── NAV ── */
  .nav {
    position:fixed; top:0; left:0; right:0; z-index:1000;
    display:flex; align-items:center; justify-content:space-between;
    padding:0 5%; height:64px;
    background:rgba(10,10,15,0.97); backdrop-filter:blur(18px);
    border-bottom:1px solid var(--border);
    gap:1rem;
  }
  .nav-logo { font-family:'Playfair Display',serif; font-size:1.5rem; color:var(--gold); cursor:pointer; flex-shrink:0; white-space:nowrap; }
  .nav-logo span { color:var(--white); }
  .nav-steps { display:flex; align-items:center; gap:0; overflow-x:auto; scrollbar-width:none; }
  .nav-steps::-webkit-scrollbar { display:none; }
  .nav-step { display:flex; align-items:center; gap:.4rem; padding:.3rem .7rem; font-size:.74rem; font-weight:600; letter-spacing:.4px; border-radius:20px; transition:all .2s; color:var(--muted); white-space:nowrap; flex-shrink:0; }
  .nav-step.done { color:var(--green); }
  .nav-step.active { color:var(--gold); background:rgba(201,168,76,.1); }
  .nav-step-num { width:20px; height:20px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:.65rem; font-weight:700; border:1.5px solid currentColor; flex-shrink:0; }
  .nav-step-arrow { color:var(--muted); font-size:.7rem; padding:0 .15rem; flex-shrink:0; }

  /* ── SUMMARY BAR ── */
  .summary-bar {
    position:sticky; top:64px; z-index:900;
    background:var(--dark2); border-bottom:1px solid var(--border);
    padding:.75rem 5%; display:flex; align-items:center; gap:1rem; flex-wrap:wrap;
  }
  .sb-item { display:flex; align-items:center; gap:.45rem; font-size:.8rem; min-width:0; }
  .sb-label { color:var(--muted); font-size:.68rem; letter-spacing:.8px; text-transform:uppercase; white-space:nowrap; }
  .sb-val { color:var(--text); font-weight:500; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:160px; }
  .sb-divider { width:1px; height:26px; background:var(--border); flex-shrink:0; }
  .sb-edit { margin-left:auto; background:transparent; color:var(--gold); border:1px solid var(--gold); font-size:.75rem; font-weight:600; padding:.32rem .9rem; border-radius:8px; cursor:pointer; transition:all .2s; white-space:nowrap; flex-shrink:0; }
  .sb-edit:hover { background:var(--gold); color:var(--dark); }
  .sb-days { background:rgba(201,168,76,.12); border:1px solid var(--gold); color:var(--gold); font-size:.75rem; font-weight:700; padding:.28rem .75rem; border-radius:20px; white-space:nowrap; flex-shrink:0; }

  /* ── MAIN LAYOUT ── */
  .step2-layout {
    display:grid;
    grid-template-columns:220px 1fr 300px;
    gap:0;
    min-height:calc(100vh - 128px);
    margin-top:128px;
  }

  /* ── FILTER PANEL ── */
  .filter-panel {
    background:var(--dark2); border-right:1px solid var(--border);
    padding:1.75rem 1.25rem; position:sticky; top:128px;
    height:calc(100vh - 128px); overflow-y:auto;
  }
  .filter-panel::-webkit-scrollbar { width:3px; }
  .filter-panel::-webkit-scrollbar-thumb { background:var(--border); border-radius:2px; }
  .fp-title { font-size:.68rem; letter-spacing:2px; text-transform:uppercase; color:var(--gold); font-weight:700; margin-bottom:1.1rem; }
  .fp-section { margin-bottom:1.75rem; }
  .fp-section h4 { font-size:.78rem; color:var(--text); font-weight:600; margin-bottom:.7rem; }
  .fp-chip { display:inline-flex; align-items:center; gap:.35rem; background:var(--surface); border:1px solid var(--border); color:var(--muted); font-size:.75rem; padding:.3rem .7rem; border-radius:20px; cursor:pointer; transition:all .2s; margin:.2rem .2rem 0 0; }
  .fp-chip.active { background:rgba(201,168,76,.15); border-color:var(--gold); color:var(--gold); }
  .fp-chip:hover { border-color:var(--gold); color:var(--gold); }
  .fp-range { width:100%; accent-color:var(--gold); height:4px; cursor:pointer; margin:.5rem 0; }
  .fp-range-labels { display:flex; justify-content:space-between; font-size:.68rem; color:var(--muted); }
  .fp-check { display:flex; align-items:center; gap:.55rem; font-size:.8rem; color:var(--muted); cursor:pointer; padding:.28rem 0; transition:color .2s; }
  .fp-check:hover { color:var(--text); }
  .fp-check input { accent-color:var(--gold); width:14px; height:14px; cursor:pointer; flex-shrink:0; }
  .fp-reset { width:100%; background:transparent; border:1px solid var(--border); color:var(--muted); font-size:.76rem; padding:.48rem; border-radius:8px; cursor:pointer; transition:all .2s; margin-top:.5rem; font-family:inherit; }
  .fp-reset:hover { border-color:var(--gold); color:var(--gold); }

  /* Mobile filter toggle button */
  .filter-toggle-btn {
    display:none;
    background:var(--surface); border:1px solid var(--border);
    color:var(--gold); font-size:.8rem; font-weight:600;
    padding:.45rem 1rem; border-radius:8px; cursor:pointer;
    align-items:center; gap:.4rem; white-space:nowrap;
    font-family:inherit; transition:all .2s;
  }
  .filter-toggle-btn:hover { border-color:var(--gold); }

  /* Mobile filter drawer */
  .filter-drawer-overlay {
    display:none; position:fixed; inset:0; background:rgba(0,0,0,.6);
    z-index:1500; backdrop-filter:blur(4px);
  }
  .filter-drawer-overlay.open { display:block; }
  .filter-drawer {
    position:fixed; left:0; top:0; bottom:0; width:280px; max-width:85vw;
    background:var(--dark2); border-right:1px solid var(--border);
    padding:1.5rem 1.25rem; overflow-y:auto; z-index:1600;
    transform:translateX(-100%); transition:transform .3s ease;
  }
  .filter-drawer.open { transform:translateX(0); }
  .filter-drawer-close {
    position:absolute; top:1rem; right:1rem;
    background:var(--surface); border:1px solid var(--border);
    color:var(--muted); width:32px; height:32px; border-radius:50%;
    cursor:pointer; font-size:1rem; display:flex; align-items:center;
    justify-content:center; transition:all .2s;
  }
  .filter-drawer-close:hover { border-color:var(--gold); color:var(--gold); }

  /* ── CARS PANEL ── */
  .cars-panel { background:var(--dark); padding:1.75rem 1.75rem; overflow-y:auto; }
  .cars-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.4rem; flex-wrap:wrap; gap:.75rem; }
  .cars-count { font-size:.83rem; color:var(--muted); }
  .cars-count strong { color:var(--gold); font-size:1.05rem; }
  .sort-select { background:var(--surface); border:1px solid var(--border); color:var(--text); font-family:inherit; font-size:.8rem; padding:.42rem .85rem; border-radius:8px; outline:none; cursor:pointer; }
  .sort-select:focus { border-color:var(--gold); }

  /* ── CAR CARD ── */
  .car-card {
    background:var(--dark2); border:1px solid var(--border);
    border-radius:14px; overflow:hidden; display:grid;
    grid-template-columns:220px 1fr; margin-bottom:1.1rem;
    transition:all .3s; cursor:pointer;
  }
  .car-card:hover { border-color:var(--gold); transform:translateY(-2px); box-shadow:0 10px 36px rgba(0,0,0,.5); }
  .car-card.selected { border-color:var(--gold); border-width:2px; box-shadow:0 0 0 3px rgba(201,168,76,.1); }
  .car-card.featured { border-color:rgba(201,168,76,.45); }
  .car-img-wrap { position:relative; overflow:hidden; background:#fff; display:flex; align-items:center; min-height:180px; }
  .car-img-wrap img { width:100%; transition:transform .5s; display:block; }
  .car-card:hover .car-img-wrap img { transform:scale(1.06); }
  .car-placeholder { width:100%; height:100%; min-height:180px; display:flex; align-items:center; justify-content:center; font-size:3rem; background:linear-gradient(135deg,var(--dark3),var(--surface)); }
  .car-badge { position:absolute; top:.65rem; left:.65rem; background:var(--gold); color:var(--dark); font-size:.65rem; font-weight:800; padding:.22rem .65rem; border-radius:20px; letter-spacing:.4px; }
  .car-badge.featured-badge { background:var(--green); color:#fff; }
  .car-body { padding:1.25rem; display:flex; flex-direction:column; }
  .car-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:.65rem; gap:.5rem; }
  .car-category { font-size:.68rem; letter-spacing:1.5px; text-transform:uppercase; color:var(--gold); font-weight:700; margin-bottom:.2rem; }
  .car-name { font-family:'Playfair Display',serif; font-size:1.2rem; color:var(--white); line-height:1.1; }
  .car-brand { font-size:.8rem; color:var(--muted); margin-top:.1rem; }
  .car-price-wrap { text-align:right; flex-shrink:0; }
  .car-price { font-family:'Playfair Display',serif; font-size:1.5rem; color:var(--gold); line-height:1; }
  .car-price small { font-family:'DM Sans',sans-serif; font-size:.68rem; color:var(--muted); display:block; font-weight:400; margin-top:.1rem; }
  .car-total { font-size:.75rem; color:var(--muted); margin-top:.12rem; }
  .car-meta { display:flex; gap:.5rem; flex-wrap:wrap; margin-bottom:.8rem; }
  .car-tag { font-size:.72rem; color:var(--muted); background:var(--dark3); padding:.2rem .55rem; border-radius:6px; display:flex; align-items:center; gap:.25rem; }
  .car-desc { font-size:.81rem; color:var(--muted); line-height:1.55; margin-bottom:.9rem; flex:1; }
  .car-actions { display:flex; gap:.6rem; margin-top:auto; }
  .btn-select { flex:1; background:var(--gold); color:var(--dark); font-weight:700; font-size:.85rem; padding:.6rem; border:none; border-radius:8px; cursor:pointer; transition:all .2s; font-family:inherit; }
  .btn-select:hover { background:var(--gold-light); }
  .btn-select.selected-btn { background:var(--green); color:#fff; }
  .btn-details { background:transparent; color:var(--gold); border:1px solid var(--gold); font-size:.8rem; font-weight:600; padding:.6rem 1rem; border-radius:8px; cursor:pointer; transition:all .2s; white-space:nowrap; font-family:inherit; }
  .btn-details:hover { background:rgba(201,168,76,.1); }

  /* ── BOOKING PANEL ── */
  .booking-panel {
    background:var(--dark2); border-left:1px solid var(--border);
    padding:1.75rem 1.25rem; position:sticky; top:128px;
    height:calc(100vh - 128px); overflow-y:auto;
    display:flex; flex-direction:column; gap:1.25rem;
  }
  .booking-panel::-webkit-scrollbar { width:3px; }
  .booking-panel::-webkit-scrollbar-thumb { background:var(--border); border-radius:2px; }
  .bp-section { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:1.1rem; }
  .bp-heading { font-size:.68rem; letter-spacing:2px; text-transform:uppercase; color:var(--gold); font-weight:700; margin-bottom:.9rem; }
  .bp-car-name { font-family:'Playfair Display',serif; font-size:1.05rem; color:var(--white); margin-bottom:.2rem; }
  .bp-car-cat { font-size:.78rem; color:var(--muted); }
  .bp-car-placeholder { width:100%; height:90px; background:var(--dark3); border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:2.2rem; margin-bottom:.9rem; }
  .bp-car-img { width:100%; height:90px; object-fit:cover; border-radius:8px; margin-bottom:.9rem; }

  /* Floating summary bar for mobile — booking panel summary */
  .booking-float {
    display:none; position:fixed; bottom:0; left:0; right:0; z-index:1400;
    background:var(--dark2); border-top:1px solid var(--border);
    padding:.9rem 4%; flex-direction:column; gap:.75rem;
  }
  // .booking-float.has-car { display:flex; }
  .bf-top { display:flex; justify-content:space-between; align-items:center; }
  .bf-name { font-weight:600; font-size:.9rem; color:var(--white); }
  .bf-price { font-family:'Playfair Display',serif; font-size:1.2rem; color:var(--gold); }
  .bf-proceed {
    width:100%; background:var(--gold); color:var(--dark);
    font-weight:700; font-size:.95rem; padding:.8rem;
    border:none; border-radius:10px; cursor:pointer; font-family:inherit;
    transition:all .2s;
  }
  .bf-proceed:hover { background:var(--gold-light); }
  .bf-details-toggle {
    background:transparent; border:none; color:var(--muted);
    font-size:.78rem; cursor:pointer; font-family:inherit; padding:.2rem 0;
    text-align:left; display:flex; align-items:center; gap:.3rem;
  }

  /* Mobile booking panel drawer */
  .booking-drawer-overlay {
    display:none; position:fixed; inset:0; background:rgba(0,0,0,.65);
    z-index:1700; backdrop-filter:blur(4px);
  }
  .booking-drawer-overlay.open { display:block; }
  .booking-drawer {
    position:fixed; left:0; right:0; bottom:0; z-index:1800;
    background:var(--dark2); border-top:1px solid var(--border);
    border-radius:18px 18px 0 0; padding:1.5rem 1.25rem 2rem;
    max-height:80vh; overflow-y:auto;
    transform:translateY(100%); transition:transform .35s ease;
  }
  .booking-drawer.open { transform:translateY(0); }
  .booking-drawer-handle { width:40px; height:4px; background:var(--border); border-radius:2px; margin:0 auto 1.25rem; }

  .extra-item { display:flex; align-items:center; gap:.65rem; padding:.55rem 0; border-bottom:1px solid var(--border); cursor:pointer; transition:all .2s; }
  .extra-item:last-child { border-bottom:none; }
  .extra-item:hover { background:rgba(201,168,76,.04); margin:0 -.5rem; padding:.55rem .5rem; border-radius:8px; }
  .extra-icon { font-size:1.2rem; flex-shrink:0; }
  .extra-info { flex:1; min-width:0; }
  .extra-name { font-size:.82rem; color:var(--text); font-weight:500; }
  .extra-desc { font-size:.7rem; color:var(--muted); }
  .extra-price { font-size:.8rem; color:var(--gold); font-weight:600; white-space:nowrap; text-align:right; }
  .extra-price small { display:block; font-size:.65rem; color:var(--muted); font-weight:400; }
  .extra-toggle { width:30px; height:17px; border-radius:9px; border:none; cursor:pointer; transition:all .2s; flex-shrink:0; position:relative; }
  .extra-toggle.on { background:var(--green); }
  .extra-toggle.off { background:var(--border); }
  .extra-toggle::after { content:''; position:absolute; top:1.5px; width:14px; height:14px; border-radius:50%; background:#fff; transition:left .2s; }
  .extra-toggle.on::after { left:14px; }
  .extra-toggle.off::after { left:1.5px; }

  .price-row { display:flex; justify-content:space-between; align-items:center; padding:.4rem 0; }
  .price-label { font-size:.81rem; color:var(--muted); }
  .price-val { font-size:.85rem; color:var(--text); font-weight:500; }
  .price-row.total { border-top:1px solid var(--border); margin-top:.25rem; padding-top:.7rem; }
  .price-row.total .price-label { color:var(--text); font-weight:600; font-size:.9rem; }
  .price-row.total .price-val { font-family:'Playfair Display',serif; font-size:1.35rem; color:var(--gold); }

  .btn-proceed {
    width:100%; background:var(--gold); color:var(--dark);
    font-family:'DM Sans',sans-serif; font-weight:700; font-size:.95rem;
    padding:.9rem; border:none; border-radius:10px; cursor:pointer;
    transition:all .25s; margin-top:auto;
  }
  .btn-proceed:hover { background:var(--gold-light); transform:translateY(-2px); box-shadow:0 10px 28px rgba(201,168,76,.3); }
  .btn-proceed:disabled { background:var(--border); color:var(--muted); transform:none; box-shadow:none; cursor:not-allowed; }
  .secure-note { font-size:.7rem; color:var(--muted); text-align:center; margin-top:.5rem; }

  .ins-item { display:flex; align-items:flex-start; gap:.65rem; padding:.6rem 0; border-bottom:1px solid var(--border); cursor:pointer; transition:all .2s; }
  .ins-item:last-child { border-bottom:none; }
  .ins-item:hover { background:rgba(201,168,76,.04); margin:0 -.5rem; padding:.6rem .5rem; border-radius:8px; }
  .ins-icon { font-size:1.2rem; flex-shrink:0; margin-top:.1rem; }
  .ins-info { flex:1; min-width:0; }
  .ins-name { font-size:.82rem; color:var(--text); font-weight:500; }
  .ins-desc { font-size:.7rem; color:var(--muted); margin-top:.08rem; }
  .ins-price { font-size:.8rem; color:var(--gold); font-weight:600; white-space:nowrap; text-align:right; min-width:56px; }
  .ins-price small { display:block; font-size:.65rem; color:var(--muted); font-weight:400; }
  .ins-radio { width:17px; height:17px; border-radius:50%; border:2px solid var(--border); flex-shrink:0; display:flex; align-items:center; justify-content:center; transition:all .2s; margin-top:.18rem; }
  .ins-radio.selected { border-color:var(--gold); background:var(--gold); }
  .ins-radio.selected::after { content:""; width:6px; height:6px; border-radius:50%; background:var(--dark); }

  .night-notice { background:rgba(239,159,39,.08); border:1px solid rgba(239,159,39,.3); border-radius:10px; padding:.7rem .9rem; font-size:.76rem; color:#EF9F27; display:flex; align-items:flex-start; gap:.5rem; }

  /* ── MODAL ── */
  .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.78); z-index:2000; display:flex; align-items:center; justify-content:center; padding:1rem; backdrop-filter:blur(4px); }
  .modal { background:var(--dark2); border:1px solid var(--border); border-radius:18px; max-width:640px; width:100%; max-height:90vh; overflow-y:auto; }
  .modal::-webkit-scrollbar { width:3px; }
  .modal::-webkit-scrollbar-thumb { background:var(--border); }
  .modal-header { padding:1.5rem 1.75rem 0; display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; }
  .modal-close { background:var(--surface); border:1px solid var(--border); color:var(--muted); width:34px; height:34px; border-radius:50%; cursor:pointer; font-size:1rem; display:flex; align-items:center; justify-content:center; transition:all .2s; flex-shrink:0; }
  .modal-close:hover { border-color:var(--gold); color:var(--gold); }
  .modal-img { width:100%; height:200px; object-fit:cover; margin-top:1.25rem; }
  .modal-body { padding:1.5rem 1.75rem 1.75rem; }
  .modal-specs { display:grid; grid-template-columns:1fr 1fr; gap:.65rem; margin:1.1rem 0; }
  .modal-spec { background:var(--surface); border:1px solid var(--border); border-radius:9px; padding:.75rem .9rem; }
  .modal-spec-label { font-size:.65rem; letter-spacing:1.5px; text-transform:uppercase; color:var(--muted); margin-bottom:.25rem; }
  .modal-spec-val { font-size:.9rem; color:var(--text); font-weight:500; }

  .empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:4rem 1.5rem; color:var(--muted); text-align:center; }
  .empty-state-icon { font-size:3.5rem; margin-bottom:.85rem; opacity:.4; }
  .empty-state h3 { font-family:'Playfair Display',serif; color:var(--text); font-size:1.3rem; margin-bottom:.4rem; }

  @keyframes shimmer { 0%{background-position:-700px 0} 100%{background-position:700px 0} }
  .skeleton { background:linear-gradient(90deg,var(--surface) 25%,var(--dark3) 50%,var(--surface) 75%); background-size:700px 100%; animation:shimmer 1.4s infinite; border-radius:6px; }

  select option { background:#1f1f26; }

  /* ═══════════════════════════════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ═══════════════════════════════════════════════════════════ */

  /* ── LARGE TABLET (≤1100px) ── */
  @media (max-width:1100px) {
    .step2-layout { grid-template-columns:190px 1fr 270px; }
  }

  /* ── TABLET (≤900px) ── */
  @media (max-width:900px) {
    /* Nav — hide step labels, show only numbers */
    .nav { padding:0 4%; height:60px; }
    .nav-logo { font-size:1.25rem; }
    .nav-step span { display:none; }
    .nav-step { padding:.28rem .4rem; }

    /* Summary bar — collapse to 2 rows */
    .summary-bar { top:60px; gap:.65rem; padding:.65rem 4%; }
    .sb-val { max-width:120px; }
    .sb-divider { display:none; }

    /* Layout — filter goes off-screen, booking panel stacks below */
    .step2-layout {
      grid-template-columns:1fr;
      margin-top:calc(60px + 52px); /* nav + smaller summary bar */
    }
    .filter-panel { display:none; } /* replaced by drawer */
    .filter-toggle-btn { display:flex; }

    /* Booking panel becomes inline (not sticky sidebar) */
    .booking-panel {
      position:relative; top:0; height:auto;
      border-left:none; border-top:1px solid var(--border);
      padding:1.5rem 4%;
      /* On tablet, show it inline below cars */
    }

    /* Car cards — horizontal layout still but narrower image */
    .car-card { grid-template-columns:180px 1fr; }
    .car-img-wrap { min-height:160px; }
    .cars-panel { padding:1.25rem 4%; }
    .booking-float.has-car { display:flex; }
    
  }

  /* ── MOBILE (≤640px) ── */
  @media (max-width:640px) {
    /* Nav */
    .nav { padding:0 3.5%; height:56px; }
    .nav-logo { font-size:1.1rem; }
    .nav-step-num { width:18px; height:18px; font-size:.6rem; }
    .nav-step-arrow { padding:0 .1rem; font-size:.65rem; }

    /* Summary bar */
    .summary-bar { top:56px; padding:.55rem 3.5%; gap:.4rem; }
    .sb-item:nth-child(n+5) { display:none; } /* hide extra items, keep pickup+dropoff+days+edit */
    .sb-val { max-width:100px; }
    .summary-bar .sb-edit { font-size:.7rem; padding:.28rem .7rem; }

    /* Layout */
    .step2-layout { margin-top:calc(56px + 46px); }

    /* Car cards — stack vertically */
    .car-card { grid-template-columns:1fr; }
    .car-img-wrap { min-height:170px; max-height:200px; }
    .car-body { padding:1rem; }
    .car-top { flex-direction:column; gap:.5rem; }
    .car-price-wrap { text-align:left; }
    .car-price { font-size:1.3rem; }

    /* Cars panel */
    .cars-panel { padding:1rem 3.5%; }
    .cars-header { gap:.5rem; }

    /* Filter drawer */
    .filter-drawer { width:290px; }

    /* Booking panel — hide on mobile (replaced by floating bar + drawer) */
    .booking-panel { display:none; }
    .booking-float { display:flex; }

    /* Modal */
    .modal { border-radius:16px 16px 0 0; max-height:92vh; }
    .modal-overlay { align-items:flex-end; padding:0; }
    .modal-specs { grid-template-columns:1fr 1fr; gap:.5rem; }
    .modal-header { padding:1.25rem 1.25rem 0; }
    .modal-body { padding:1.25rem; }
  }

  /* ── MOBILE SMALL (≤400px) ── */
  @media (max-width:400px) {
    .nav { padding:0 3%; }
    .nav-logo { font-size:1rem; }
    .summary-bar { padding:.5rem 3%; }
    .step2-layout { margin-top:calc(56px + 42px); }
    .cars-panel { padding:.9rem 3%; }
    .car-body { padding:.85rem; }
    .modal-specs { grid-template-columns:1fr; }
    .filter-drawer { width:85vw; }
    .booking-drawer { padding:1.25rem 1rem 2.5rem; }
    .bf-proceed { font-size:.88rem; }
  }

  .nav-steps-container{
    width:100%;
    display:flex;
    justify-content:center;
    margin-top:20px;
  }
  
  .nav-steps{
    display:flex;
    align-items:center;
    flex-wrap:wrap;
    gap:10px;
    font-size:1rem;
    margin-top:60px;
  }
  .summary-bar{
    margin-top:10px;
  }
`;

// ─── HELPERS ──────────────────────────────────────────────────
const CAR_EMOJI = { "Small Car": "🚗", "SUV": "🚙", "7 Seater": "🚐", "Luxury": "🏎️", "Default": "🚘" };

function getImg(path) {
  if (path && path !== "" && path !== "default.jpg") return `http://localhost:5000/${path}`;
  return null;
}

function fmtPrice(n) {
  if (n === undefined || n === null || isNaN(n)) return "₹0";
  return "₹" + Number(n).toLocaleString("en-IN");
}

function isNightTime(timeStr) {
  const h = parseInt(timeStr?.split(":")[0] ?? "0");
  return h >= 22 || h < 7;
}

function getExtraIcon(name = "") {
  const n = name.toLowerCase();
  if (n.includes("gps") || n.includes("nav"))     return "🗺️";
  if (n.includes("child") || n.includes("baby"))  return "👶";
  if (n.includes("driver"))                        return "👤";
  if (n.includes("insur"))                         return "🛡️";
  if (n.includes("road") || n.includes("assist")) return "🔧";
  if (n.includes("wifi"))                          return "📶";
  if (n.includes("fuel"))                          return "⛽";
  return "➕";
}

function mapExtras(rawExtras) {
  let source;
  if (Array.isArray(rawExtras?.data)) source = rawExtras.data;
  else if (Array.isArray(rawExtras))  source = rawExtras;
  else                                source = mockExtras;

  return source.map(e => {
    if (e.optdet_id !== undefined) {
      const rt       = (e.rate_type || "").toLowerCase().replace(/\s+/g, "");
      const isPerDay = rt === "/day" || rt === "perday" || rt === "day" || rt === "daily";
      const isFree   = parseInt(e.opt_type) === 1;
      const price    = isFree ? 0 : (parseFloat(e.opt_dayrate) || 0);
      return { id: e.optdet_id, name: e.optitem_name, icon: getExtraIcon(e.optitem_name), price, unit: isPerDay ? "per day" : "total", desc: isFree ? "Included free with your rental" : `Max ${e.opt_max} · Min ${e.min_bkngdys} booking day(s)`, isFree, selected: false };
    }
    return { ...e, isFree: false, selected: false };
  });
}

// function resolveInsurancePrice(ins, days, daySlabs) {
//   if (!daySlabs || !daySlabs.length) return parseFloat(ins.ins_chargeperday || 0) * days;
//   const slab = daySlabs.find(s => days >= parseInt(s.FromIns_slab) && days <= parseInt(s.ToIns_slab));
//   if (slab && ins.slab_prices?.[slab.Insuranceslabid] !== undefined) return parseFloat(ins.slab_prices[slab.Insuranceslabid]) || 0;
//   return parseFloat(ins.ins_chargeperday || 0) * days;
// }

// function resolveInsurancePrice(ins, days, daySlabs) {
//   if (!daySlabs || !daySlabs.length) return parseFloat(ins.ins_chargeperday || 0) * days;
//   const slab = daySlabs.find(s => days >= parseInt(s.FromIns_slab) && days <= parseInt(s.ToIns_slab));
//   if (slab && ins.slab_prices?.[slab.Insuranceslabid] !== undefined) return parseFloat(ins.slab_prices[slab.Insuranceslabid]) || 0;
//   return parseFloat(ins.ins_chargeperday || 0) * days;
// }
function resolveInsurancePrice(ins, days) {

  const dailyPrice =
    parseFloat(ins.ins_chargeperday || 0);

  return dailyPrice * Number(days || 1);

}


// ADD this new helper below it:
// function totalInsurancePrice(selectedInsurances, days, daySlabs) {
//   return (selectedInsurances || []).reduce((sum, ins) => sum + resolveInsurancePrice(ins, days, daySlabs), 0);
// }
function totalInsurancePrice(selectedInsurances, days) {

  return (selectedInsurances || []).reduce(
    (sum, ins) => sum + resolveInsurancePrice(ins, days),
    0
  );

}

function mapInsurance(rawInsurance) {
  if (Array.isArray(rawInsurance?.data)) return rawInsurance.data;
  if (Array.isArray(rawInsurance))       return rawInsurance;
  return mockInsurance;
}

// ─── STEP NAV ─────────────────────────────────────────────────
// function StepNav({ siteName }) {
//   const steps = ["Location", "Choose Car", "Reserve", "Payment", "Confirm"];
//   return (
//     <nav className="nav">
//       <div className="nav-logo">Orisnz<span>{siteName || "-Fleet"}</span></div>
//       <div className="nav-steps">
//         {steps.map((s, i) => (
//           <div key={s} style={{ display: "flex", alignItems: "center" }}>
//             <div className={`nav-step ${i === 0 ? "done" : i === 1 ? "active" : ""}`}>
//               <div className="nav-step-num">{i === 0 ? "✓" : i + 1}</div>
//               <span>{s}</span>
//             </div>
//             {i < steps.length - 1 && <div className="nav-step-arrow">›</div>}
//           </div>
//         ))}
//       </div>
//     </nav>
//   );
// }
const STEPS = ["Location", "Choose Car", "Reserve", "Payment", "Confirm"];

function StepNav({ siteName, onLogoClick }) {
  const [settings, setSettings] = useState({
    site_name: "-Fleet",
    currency: "₹",
  });

  return (
    <>
      <Navbar
        activePage="Fleet"
        siteName={settings?.site_name}
      />

      <div className="nav-steps-container">
        <div className="nav-steps">
          {STEPS.map((s, i) => (
            <div
              key={s}
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                className={`nav-step ${i === 0 ? "done" : i === 1 ? "active" : ""}`}
              >
                <div className="nav-step-num">
                {i === 0 ? "✓" : i + 1}
                </div>

                <span>{s}</span>
              </div>

              {i < STEPS.length - 1 && (
                <div className="nav-step-arrow">›</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── SUMMARY BAR ─────────────────────────────────────────────
function SummaryBar({ ctx, onEdit, onFilterToggle }) {
  return (
    <div className="summary-bar">
      <button className="filter-toggle-btn" onClick={onFilterToggle}>
        ⚙ Filters
      </button>
      <div className="sb-item">
        <span>📍</span>
        <div>
          <div className="sb-label">Pickup</div>
          <div className="sb-val">{ctx.pickup}</div>
        </div>
      </div>
      <div className="sb-divider" />
      <div className="sb-item">
        <span>🏁</span>
        <div>
          <div className="sb-label">Drop-off</div>
          <div className="sb-val">{ctx.dropoff || ctx.pickup}</div>
        </div>
      </div>
      <div className="sb-divider" />
      <div className="sb-item">
        <span>📅</span>
        <div>
          <div className="sb-label">From</div>
          <div className="sb-val">{ctx.pickupDate} {ctx.pickupTime}</div>
        </div>
      </div>
      <div className="sb-divider" />
      <div className="sb-item">
        <span>📅</span>
        <div>
          <div className="sb-label">To</div>
          <div className="sb-val">{ctx.returnDate} {ctx.returnTime}</div>
        </div>
      </div>
      <div className="sb-divider" />
      <div className="sb-days">{ctx.days} day{ctx.days > 1 ? "s" : ""}</div>
      <button className="sb-edit" onClick={onEdit}>✏ Edit</button>
    </div>
  );
}

// ─── FILTER PANEL CONTENT (shared between sidebar and drawer) ──
function FilterContent({ groups, filters, onChange, maxPrice, onReset }) {
  return (
    <>
      <div className="fp-title">Filters</div>
      <div className="fp-section">
        <h4>Category</h4>
        {groups.map(g => (
          <button key={g.group_id} className={`fp-chip ${filters.group === g.group_id ? "active" : ""}`} onClick={() => onChange("group", g.group_id)}>
            {CAR_EMOJI[g.group_name] || "🚗"} {g.group_name}
          </button>
        ))}
      </div>
      <div className="fp-section">
        <h4>Max Price / Day</h4>
        <input type="range" min={500} max={maxPrice} step={100} value={filters.maxPrice} onChange={e => onChange("maxPrice", Number(e.target.value))} className="fp-range" />
        <div className="fp-range-labels"><span>₹500</span><span>{fmtPrice(filters.maxPrice)}</span></div>
      </div>
      <div className="fp-section">
        <h4>Transmission</h4>
        {["Automatic", "Manual"].map(t => (
          <label key={t} className="fp-check">
            <input type="checkbox" checked={filters.transmissions.includes(t)} onChange={() => {
              const next = filters.transmissions.includes(t) ? filters.transmissions.filter(x => x !== t) : [...filters.transmissions, t];
              onChange("transmissions", next);
            }} />
            {t}
          </label>
        ))}
      </div>
      <div className="fp-section">
        <h4>Seats</h4>
        {["4", "5", "7"].map(s => (
          <label key={s} className="fp-check">
            <input type="checkbox" checked={filters.seats.includes(s)} onChange={() => {
              const next = filters.seats.includes(s) ? filters.seats.filter(x => x !== s) : [...filters.seats, s];
              onChange("seats", next);
            }} />
            {s} Seater
          </label>
        ))}
      </div>
      <div className="fp-section">
        <label className="fp-check">
          <input type="checkbox" checked={filters.acOnly} onChange={e => onChange("acOnly", e.target.checked)} />
          Air Conditioned only
        </label>
      </div>
      <button className="fp-reset" onClick={onReset}>Reset Filters</button>
    </>
  );
}

// ─── FILTER PANEL (sidebar) ───────────────────────────────────
function FilterPanel({ groups, filters, onChange, maxPrice, onReset }) {
  return (
    <aside className="filter-panel">
      <FilterContent groups={groups} filters={filters} onChange={onChange} maxPrice={maxPrice} onReset={onReset} />
    </aside>
  );
}

// ─── FILTER DRAWER (mobile) ───────────────────────────────────
function FilterDrawer({ open, onClose, groups, filters, onChange, maxPrice, onReset }) {
  return (
    <>
      <div className={`filter-drawer-overlay${open ? " open" : ""}`} onClick={onClose} />
      <div className={`filter-drawer${open ? " open" : ""}`}>
        <button className="filter-drawer-close" onClick={onClose}>✕</button>
        <FilterContent groups={groups} filters={filters} onChange={onChange} maxPrice={maxPrice} onReset={onReset} />
      </div>
    </>
  );
}

// ─── CAR CARD ─────────────────────────────────────────────────
function CarCard({ car, selected, days, ctx, onSelect, onDetails, onPriceFetched }) {
  const img = getImg(car.car_image);
  const [price, setPrice] = useState(car.base_price);
  const [loadingPrice, setLoadingPrice] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoadingPrice(true);
    fetchCarPrice(car, ctx).then(p => {
      if (mounted) { setPrice(p); setLoadingPrice(false); onPriceFetched(car.car_id, p); }
    });
    return () => { mounted = false; };
  }, [car.car_id, ctx.days]);

  const total = (price || 0) * (days || 1);
  const isSelected = selected?.car_id === car.car_id;

  return (
    <div className={`car-card ${isSelected ? "selected" : ""} ${car.featured ? "featured" : ""}`} onClick={() => onSelect({ ...car, base_price: price })}>
      <div className="car-img-wrap">
        {img ? <img src={img} alt={car.image_alt || car.car_name} /> : <div className="car-placeholder">🚘</div>}
        <div className={`car-badge ${car.featured ? "featured-badge" : ""}`}>{car.featured ? "⭐ Recommended" : car.badge}</div>
      </div>
      <div className="car-body">
        <div className="car-top">
          <div>
            <div className="car-category">{car.cat_name}</div>
            <div className="car-name">{car.car_name}</div>
            <div className="car-brand">{car.car_brand}</div>
          </div>
          <div className="car-price-wrap">
            <div className="car-price">
              {loadingPrice ? <div className="skeleton" style={{ width: 75, height: 18 }} /> : fmtPrice(price)}
              <small>per day</small>
            </div>
            <div className="car-total">
              {loadingPrice ? <div className="skeleton" style={{ width: 90, height: 13, marginTop: 4 }} /> : <>Total {fmtPrice(total)}</>}
            </div>
          </div>
        </div>
        {(car.car_passgrno || car.car_luggno || car.car_transmission || car.car_airconditionr) && (
          <div className="car-meta">
            {car.car_passgrno && <span className="car-tag">👥 {car.car_passgrno}</span>}
            {car.car_luggno   && <span className="car-tag">🧳 {car.car_luggno}</span>}
            {car.car_transmission && <span className="car-tag">⚙️ {car.car_transmission}</span>}
            {car.car_airconditionr === "Yes" && <span className="car-tag">❄️ A/C</span>}
          </div>
        )}
        <p className="car-desc">{car.car_desc}</p>
        <div className="car-actions">
          <button className={`btn-select ${isSelected ? "selected-btn" : ""}`} onClick={e => { e.stopPropagation(); onSelect({ ...car, base_price: price }); }}>
            {isSelected ? "✓ Selected" : "Select Vehicle"}
          </button>
          <button className="btn-details" onClick={e => { e.stopPropagation(); onDetails({ ...car, base_price: price }); }}>Details</button>
        </div>
      </div>
    </div>
  );
}

// ─── CAR DETAILS MODAL ────────────────────────────────────────
function CarModal({ car, days, onClose, onSelect }) {
  const img = getImg(car.car_image);
  const specs = [
    { label: "Category",     val: car.cat_name },
    { label: "Brand",        val: car.car_brand },
    { label: "Passengers",   val: car.car_passgrno ? `${car.car_passgrno} seats` : "—" },
    { label: "Luggage",      val: car.car_luggno ? `${car.car_luggno} bags` : "—" },
    { label: "Transmission", val: car.car_transmission || "—" },
    { label: "Air Con",      val: car.car_airconditionr === "Yes" ? "Yes" : "No" },
    { label: "Group",        val: car.group_name },
    { label: "Daily Rate",   val: fmtPrice(car?.base_price) },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div style={{ fontSize: ".68rem", letterSpacing: "2px", textTransform: "uppercase", color: "var(--gold)", fontWeight: 700, marginBottom: ".25rem" }}>{car.cat_name}</div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.5rem", color: "var(--white)" }}>{car.car_name}</div>
            <div style={{ fontSize: ".82rem", color: "var(--muted)" }}>{car.car_brand} · {car.group_name}</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {img
          ? <img src={img} alt={car.image_alt} className="modal-img" />
          : <div style={{ width: "100%", height: 180, background: "var(--dark3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "3.5rem", marginTop: "1.25rem" }}>{CAR_EMOJI[car.group_name] || "🚘"}</div>
        }
        <div className="modal-body">
          {car.car_desc && <p style={{ fontSize: ".9rem", color: "var(--muted)", lineHeight: 1.6, marginBottom: "1.25rem" }}>{car.car_desc}</p>}
          <div className="modal-specs">
            {specs.map(s => (
              <div key={s.label} className="modal-spec">
                <div className="modal-spec-label">{s.label}</div>
                <div className="modal-spec-val">{s.val}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: ".75rem", marginTop: "1.25rem", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 120, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 9, padding: ".9rem", textAlign: "center" }}>
              <div style={{ fontSize: ".68rem", color: "var(--muted)", marginBottom: ".2rem" }}>Daily Rate</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.3rem", color: "var(--gold)" }}>{fmtPrice(car?.base_price)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 120, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 9, padding: ".9rem", textAlign: "center" }}>
              <div style={{ fontSize: ".68rem", color: "var(--muted)", marginBottom: ".2rem" }}>{days}-Day Total</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.3rem", color: "var(--gold)" }}>{fmtPrice((car?.base_price || 0) * (days || 1))}</div>
            </div>
          </div>
          <button className="btn-select" style={{ width: "100%", marginTop: "1.1rem", padding: ".8rem" }} onClick={() => { onSelect(car); onClose(); }}>
            Select This Vehicle →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── BOOKING PANEL CONTENT (shared between sidebar and mobile drawer) ──
function BookingContent({ ctx, selectedCar, extras, onToggleExtra, insuranceList, daySlabs, selectedInsurance, onSelectInsurance, onProceed }) {
  const nightPickup = isNightTime(ctx.pickupTime);
  const nightReturn = isNightTime(ctx.returnTime);
  const nightCharge = 250;

  const baseTotal      = selectedCar ? selectedCar.base_price * ctx.days : 0;
  const nightTotal     = (nightPickup ? nightCharge : 0) + (nightReturn ? nightCharge : 0);
  const extrasTotal    = extras.filter(e => e.selected && !e.isFree).reduce((s, e) => s + (e.unit === "per day" ? e.price * ctx.days : e.price), 0);
  // const insurancePrice = selectedInsurance ? resolveInsurancePrice(selectedInsurance, ctx.days, daySlabs) : 0;
  // const grandTotal     = baseTotal + nightTotal + extrasTotal + insurancePrice;
  const insurancePrice = totalInsurancePrice(selectedInsurance, ctx.days, daySlabs);
  const grandTotal     = baseTotal + nightTotal + extrasTotal + insurancePrice;

  return (
    <>
      <div className="bp-section">
        <div className="bp-heading">Selected Vehicle</div>
        {selectedCar ? (
          <>
            {getImg(selectedCar.car_image)
              ? <img src={getImg(selectedCar.car_image)} alt={selectedCar.car_name} className="bp-car-img" />
              : <div className="bp-car-placeholder">{CAR_EMOJI[selectedCar.group_name] || "🚘"}</div>
            }
            <div className="bp-car-name">{selectedCar.car_name}</div>
            <div className="bp-car-cat">{selectedCar.car_brand} · {selectedCar.cat_name}</div>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "1.25rem 0", color: "var(--muted)", fontSize: ".85rem" }}>
            <div style={{ fontSize: "1.8rem", marginBottom: ".4rem", opacity: .4 }}>🚗</div>
            No vehicle selected yet
          </div>
        )}
      </div>

      {(nightPickup || nightReturn) && (
        <div className="night-notice">
          <span>🌙</span>
          <span>Night charge for {nightPickup && "pickup"}{nightPickup && nightReturn && " & "}{nightReturn && "return"} outside 07:00–22:00.<strong style={{ display: "block" }}>+{fmtPrice(nightCharge)} per instance</strong></span>
        </div>
      )}

      <div className="bp-section">
        <div className="bp-heading">Optional Extras</div>
        {extras.map(ex => {
          const lineTotal = ex.isFree ? 0 : ex.unit === "per day" ? ex.price * ctx.days : ex.price;
          return (
            <div key={ex.id} className="extra-item" onClick={() => onToggleExtra(ex.id)}>
              <span className="extra-icon">{ex.icon}</span>
              <div className="extra-info">
                <div className="extra-name">{ex.name}</div>
                <div className="extra-desc">{ex.desc}</div>
              </div>
              <div className="extra-price">
                {ex.isFree ? <span style={{ color: "var(--green)", fontWeight: 700 }}>Free</span> : fmtPrice(lineTotal)}
                {!ex.isFree && <small>{ex.unit === "per day" ? `${fmtPrice(ex.price)}/day×${ctx.days}` : "flat"}</small>}
              </div>
              <button className={`extra-toggle ${ex.selected ? "on" : "off"}`} onClick={e => { e.stopPropagation(); onToggleExtra(ex.id); }} />
            </div>
          );
        })}
      </div>

      {/* <div className="bp-section">
        <div className="bp-heading">Insurance</div>
        <div className="ins-item" onClick={() => onSelectInsurance(null)}>
          <span className="ins-icon">🚫</span>
          <div className="ins-info"><div className="ins-name">No Insurance</div><div className="ins-desc">I'll take my chances (not recommended)</div></div>
          <div className="ins-price" style={{ color: "var(--muted)" }}>—</div>
          <div className={`ins-radio ${!selectedInsurance ? "selected" : ""}`} />
        </div>
        {insuranceList.map(ins => {
          const price = resolveInsurancePrice(ins, ctx.days, daySlabs);
          const isChosen = selectedInsurance?.ins_id === ins.ins_id;
          return (
            <div key={ins.ins_id} className="ins-item" onClick={() => onSelectInsurance(ins)}>
              <span className="ins-icon">🛡️</span>
              <div className="ins-info">
                <div className="ins-name">{ins.ins_item}</div>
                <div className="ins-desc">{daySlabs.length ? `Slab rate for ${ctx.days} day${ctx.days > 1 ? "s" : ""}` : `${fmtPrice(ins.ins_chargeperday)}/day`}</div>
              </div>
              <div className="ins-price">{fmtPrice(price)}<small>total</small></div>
              <div className={`ins-radio ${isChosen ? "selected" : ""}`} />
            </div>
          );
        })}
      </div> */}

      <div className="bp-section">
        <div className="bp-heading">Insurance</div>
        {insuranceList.length === 0 && (
          <div style={{ fontSize: ".8rem", color: "var(--muted)", textAlign: "center", padding: ".5rem 0" }}>
            No insurance options available
          </div>
        )}
        {insuranceList.map(ins => {
          // const price    = resolveInsurancePrice(ins, ctx.days, daySlabs);
          const price    = resolveInsurancePrice(ins, ctx.days);
          const isChosen = (selectedInsurance || []).some(s => s.ins_id === ins.ins_id);
          return (
            <div key={ins.ins_id} className="extra-item" onClick={() => onSelectInsurance(ins)}>
              <span className="extra-icon">🛡️</span>
              <div className="extra-info">
                <div className="extra-name">{ins.ins_item}</div>
                <div className="extra-desc">
                  {daySlabs.length
                    ? `Slab rate for ${ctx.days} day${ctx.days > 1 ? "s" : ""}`
                    : `${fmtPrice(ins.ins_chargeperday)}/day`}
                </div>
              </div>
              <div className="extra-price">
                {fmtPrice(price)}
                <small>total</small>
              </div>
              <button
                className={`extra-toggle ${isChosen ? "on" : "off"}`}
                onClick={e => { e.stopPropagation(); onSelectInsurance(ins); }}
              />
            </div>
          );
        })}
      </div>

      <div className="bp-section">
        <div className="bp-heading">Price Summary</div>
        {selectedCar && <div className="price-row"><span className="price-label">{fmtPrice(selectedCar.base_price)} × {ctx.days} days</span><span className="price-val">{fmtPrice(baseTotal)}</span></div>}
        {nightTotal > 0 && <div className="price-row"><span className="price-label">Night charges</span><span className="price-val">{fmtPrice(nightTotal)}</span></div>}
        {extras.filter(e => e.selected).map(e => (
          <div key={e.id} className="price-row">
            <span className="price-label">{e.name}</span>
            <span className="price-val">{e.isFree ? <span style={{color:"var(--green)"}}>Free</span> : fmtPrice(e.unit === "per day" ? e.price * ctx.days : e.price)}</span>
          </div>
        ))}
        {/* {selectedInsurance && <div className="price-row"><span className="price-label">🛡️ {selectedInsurance.ins_item}</span><span className="price-val">{fmtPrice(insurancePrice)}</span></div>} */}
        {(selectedInsurance || []).map(ins => (
        <div key={ins.ins_id} className="price-row">
        <span className="price-label">🛡️ {ins.ins_item}</span>
        <span className="price-val">{fmtPrice(resolveInsurancePrice(ins, ctx.days, daySlabs))}</span>
        </div>
        ))}
        {!selectedCar && <div style={{ fontSize: ".8rem", color: "var(--muted)", textAlign: "center", padding: ".4rem 0" }}>Select a vehicle to see pricing</div>}
        <div className="price-row total">
          <span className="price-label">Total Estimate</span>
          <span className="price-val">{selectedCar ? fmtPrice(grandTotal) : "—"}</span>
        </div>
      </div>

      <div style={{ marginTop: "auto" }}>
        <button className="btn-proceed" disabled={!selectedCar} onClick={onProceed}>
          {selectedCar ? "Proceed to Reserve →" : "Select a Vehicle First"}
        </button>
        <div className="secure-note">🔒 Secure booking · Free cancellation</div>
      </div>
    </>
  );
}

// ─── BOOKING PANEL (sidebar) ──────────────────────────────────
function BookingPanel(props) {
  return (
    <aside className="booking-panel">
      <BookingContent {...props} />
    </aside>
  );
}

// ─── FLOATING BOOKING BAR + DRAWER (mobile) ───────────────────
function BookingFloat({ selectedCar, ctx, extras, insuranceList, daySlabs, selectedInsurance, onSelectInsurance, onToggleExtra, onProceed }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const nightCharge    = 250;
  const nightPickup    = isNightTime(ctx.pickupTime);
  const nightReturn    = isNightTime(ctx.returnTime);
  const baseTotal      = selectedCar ? selectedCar.base_price * ctx.days : 0;
  const nightTotal     = (nightPickup ? nightCharge : 0) + (nightReturn ? nightCharge : 0);
  const extrasTotal    = extras.filter(e => e.selected && !e.isFree).reduce((s, e) => s + (e.unit === "per day" ? e.price * ctx.days : e.price), 0);
  // const insurancePrice = selectedInsurance ? resolveInsurancePrice(selectedInsurance, ctx.days, daySlabs) : 0;
  // const grandTotal     = baseTotal + nightTotal + extrasTotal + insurancePrice;
  const insurancePrice = totalInsurancePrice(selectedInsurance, ctx.days, daySlabs);
  const grandTotal     = baseTotal + nightTotal + extrasTotal + insurancePrice;

  
  return (
    <>
      {/* Floating bottom bar — only visible when car selected on mobile */}
      <div className={`booking-float${selectedCar ? " has-car" : ""}`}>
        <div className="bf-top">
          <div>
            <div className="bf-name">{selectedCar?.car_name}</div>
            <button className="bf-details-toggle" onClick={() => setDrawerOpen(true)}>
              Extras & Insurance ▲
            </button>
          </div>
          <div className="bf-price">{fmtPrice(grandTotal)}</div>
        </div>
        <button className="bf-proceed" onClick={onProceed}>Proceed to Reserve →</button>
      </div>

      {/* Mobile booking drawer */}
      <div className={`booking-drawer-overlay${drawerOpen ? " open" : ""}`} onClick={() => setDrawerOpen(false)} />
      <div className={`booking-drawer${drawerOpen ? " open" : ""}`}>
        <div className="booking-drawer-handle" />
        <BookingContent
          ctx={ctx}
          selectedCar={selectedCar}
          extras={extras}
          onToggleExtra={onToggleExtra}
          insuranceList={insuranceList}
          daySlabs={daySlabs}
          selectedInsurance={selectedInsurance}
          onSelectInsurance={onSelectInsurance}
          onProceed={() => { setDrawerOpen(false); onProceed(); }}
        />
      </div>
    </>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────
export default function BookingStep2() {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: rawCars,      loading: carsLoading   } = useFetch("/cars?limit=100", mockCars);
  const { data: rawGroups                             } = useFetch("/cargroup", mockGroups.slice(1));
  const { data: rawExtras                             } = useFetch("/optional-extras", mockExtras);
  const { data: rawInsurance                          } = useFetch("/insurance", mockInsurance);
  const { data: rawDaySlabs                           } = useFetch("/insurance/day-slabs", []);

  const [selectedCar,       setSelectedCar      ] = useState(null);
  const [modalCar,          setModalCar         ] = useState(null);
  const [sortBy,            setSortBy           ] = useState("price_asc");
  const [carPrices,         setCarPrices        ] = useState({});
  // const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [selectedInsurance, setSelectedInsurance] = useState([]);
  const [filterDrawerOpen,  setFilterDrawerOpen ] = useState(false);
  const [extras,            setExtras           ] = useState(() => mapExtras(mockExtras));

  useEffect(() => { if (rawExtras) setExtras(mapExtras(rawExtras)); }, [rawExtras]);

  const maxPossiblePrice = 8000;
  const [filters, setFilters] = useState({ group: 0, maxPrice: maxPossiblePrice, transmissions: [], seats: [], acOnly: false });

  const [ctx] = useState(() => {
    const data = location.state;
    if (!data) return mockBookingContext;
    const start    = new Date(`${data.pickupDate}T${data.pickupTime || "00:00"}`);
    const end      = new Date(`${data.returnDate}T${data.returnTime || "00:00"}`);
    const diffDays = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    return { ...data, days: diffDays };
  });

  const groupList = Array.isArray(rawGroups?.data) ? rawGroups.data : Array.isArray(rawGroups) ? rawGroups : [];
  const groups    = [{ group_id: 0, group_name: "All Categories" }, ...groupList];

  const carList = Array.isArray(rawCars?.data) ? rawCars.data : Array.isArray(rawCars) ? rawCars : mockCars;
  const cars = carList
    .filter(c => {
      if (filters.group !== 0 && c.group_id !== filters.group) return false;
      const effectivePrice = carPrices[c.car_id] ?? c.base_price;
      if (effectivePrice > filters.maxPrice) return false;
      if (filters.transmissions.length && !filters.transmissions.includes(c.car_transmission)) return false;
      if (filters.seats.length && !filters.seats.includes(String(c.car_passgrno))) return false;
      if (filters.acOnly && c.car_airconditionr !== "Yes") return false;
      return true;
    })
    .sort((a, b) => {
      const priceA = carPrices[a.car_id] ?? a.base_price;
      const priceB = carPrices[b.car_id] ?? b.base_price;
      if (sortBy === "price_asc")  return priceA - priceB;
      if (sortBy === "price_desc") return priceB - priceA;
      if (sortBy === "seats")      return b.car_passgrno - a.car_passgrno;
      return 0;
    });

  const insuranceList = mapInsurance(rawInsurance);
  const daySlabs      = Array.isArray(rawDaySlabs?.data) ? rawDaySlabs.data : Array.isArray(rawDaySlabs) ? rawDaySlabs : [];

  const changeFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));
  const resetFilters = () => setFilters({ group: 0, maxPrice: maxPossiblePrice, transmissions: [], seats: [], acOnly: false });
  const toggleExtra  = id => setExtras(ex => ex.map(e => e.id === id ? { ...e, selected: !e.selected } : e));
  const handlePriceFetched = (carId, price) => setCarPrices(prev => ({ ...prev, [carId]: price }));

  const handleProceed = () => {
    if (!selectedCar) return;
    // const booking = { ...ctx, selectedCar, selectedExtras: extras.filter(e => e.selected), selectedInsurance };
    const booking = { ...ctx, selectedCar, selectedExtras: extras.filter(e => e.selected), selectedInsurance: selectedInsurance || [] };
    
    navigate("/booking-step3", { state: booking });
    alert(`Proceeding to reserve ${selectedCar.car_name}!\n\nIn production this navigates to Step 3 (Reserve).`);
  };

  const bookingPanelProps = {
    ctx, selectedCar, extras, onToggleExtra: toggleExtra,
    insuranceList, daySlabs, selectedInsurance,
    // onSelectInsurance: setSelectedInsurance,
    onSelectInsurance: (ins) => {
      setSelectedInsurance(prev => {
        const already = (prev || []).some(s => s.ins_id === ins.ins_id);
        return already
          ? (prev || []).filter(s => s.ins_id !== ins.ins_id)  // deselect
          : [...(prev || []), ins];                             // select
      });
    },
     onProceed: handleProceed,
  };

  return (
    <>
      <style>{css}</style>
      <StepNav siteName="-Fleet" />
      <SummaryBar ctx={ctx} onEdit={() => navigate("/home", { state: ctx })} onFilterToggle={() => setFilterDrawerOpen(true)} />

      <div className="step2-layout">
        {/* Desktop sidebar filter */}
        <FilterPanel groups={groups} filters={filters} onChange={changeFilter} maxPrice={maxPossiblePrice} onReset={resetFilters} />

        {/* Cars list */}
        <main className="cars-panel">
          <div className="cars-header">
            <div className="cars-count"><strong>{cars.length}</strong> vehicle{cars.length !== 1 ? "s" : ""} available</div>
            <select className="sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="seats">Most Seats</option>
            </select>
          </div>

          {carsLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} style={{ height: 180, marginBottom: "1.1rem", borderRadius: 14 }} className="skeleton" />
              ))
            : cars.length === 0
              ? (
                <div className="empty-state">
                  <div className="empty-state-icon">🔍</div>
                  <h3>No vehicles match your filters</h3>
                  <p style={{ fontSize: ".85rem", marginTop: ".4rem" }}>Try adjusting the category or price range</p>
                  <button className="sb-edit" style={{ marginTop: "1.25rem" }} onClick={resetFilters}>Clear Filters</button>
                </div>
              )
              : cars.map(car => (
                <CarCard key={car.car_id} car={car} selected={selectedCar} days={ctx.days} ctx={ctx} onSelect={setSelectedCar} onDetails={setModalCar} onPriceFetched={handlePriceFetched} />
              ))
          }
        </main>

        {/* Desktop booking panel */}
        <BookingPanel {...bookingPanelProps} />
      </div>

      {/* Mobile filter drawer */}
      <FilterDrawer open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)} groups={groups} filters={filters} onChange={changeFilter} maxPrice={maxPossiblePrice} onReset={resetFilters} />

      {/* Mobile floating booking bar + drawer */}
      <BookingFloat
        selectedCar={selectedCar}
        ctx={ctx}
        extras={extras}
        insuranceList={insuranceList}
        daySlabs={daySlabs}
        selectedInsurance={selectedInsurance}
        onSelectInsurance={setSelectedInsurance}
        onToggleExtra={toggleExtra}
        onProceed={handleProceed}
      />

      {modalCar && (
        <CarModal car={modalCar} days={ctx.days} onClose={() => setModalCar(null)} onSelect={car => { setSelectedCar(car); setModalCar(null); }} />
      )}
    </>
  );
}